import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Headline, Accordion } from '../../../design-system';
import {
  FiltersWrapper,
  FiltersList,
  FilterHeadWrapper,
  TitleAndClose,
  Title,
  HideFilters,
} from './Filters.style';
import { useAvailableFilterOptions } from '../../hooks/useAvailableFilterOptions';
import { ALL_FILTERS_OPENED, INTIAL_FILTERS } from '../../constants/initialFilters';
import {
  addOrRemoveTempFilter,
  selectFilters,
  clearAllFilters,
} from '../../reducers/filtersReducer';
import CtaGroup from './CtaGroup';
import { MultipleSelectChangeEventComponent } from '../../types/analytics';
import {
  filterChange,
  filterClearAll,
  filterCurrentState,
} from '../../utils/analyticsEventListeners';
import CloseIcon from '../../assets/icons/CloseIcon';
import BackIcon from '../../assets/icons/BackIcon';
import ZipInput from './ZipInput';
import useScrollTracker from '../../hooks/useScrollTracker';
import getFinalFiltersList from '../../utils/getFinalFiltersList';
import { selectOffers } from '../../reducers/offersReducer';
import useFiltersList from '../../hooks/useFiltersList';
import { selectZipCode } from '../../reducers/zipCodeReducer';

type FiltersProps = {
  isLaptop: boolean;
  isReachedEndOfContainer: boolean;
  setIsFiltersOpen: (arg: boolean) => void;
};

const Filters: React.FC<FiltersProps> = ({
  isLaptop,
  isReachedEndOfContainer,
  setIsFiltersOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openedFiltersList, setOpenedFiltersList] = useState(ALL_FILTERS_OPENED);

  const { confirmedFilters } = useSelector(selectFilters);
  const { offersList } = useSelector(selectOffers);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const listRef = useRef(null);

  const { totalAvailableOffers } = useAvailableFilterOptions();

  const handleAddFilter = (filterType: string, filterValue: string, isChecked?: boolean) => {
    dispatch(addOrRemoveTempFilter({ filterType, filterValue }));
    const payloadForIxis: MultipleSelectChangeEventComponent = {
      action: !isChecked ? 'select' : 'deselect',
      filterName: filterType,
      value: filterValue,
    };

    const { latestFilterState, totalCount } = getFinalFiltersList(
      filterType,
      filterValue,
      confirmedFilters,
      offersList,
    );

    filterChange({ payloadForIxis, latestFilterState, totalCount });
  };

  const handleExposeFilter = (filterType: string) => {
    setOpenedFiltersList((prevState) => ({ ...prevState, [filterType]: !prevState[filterType] }));
  };

  const handleCloseFilters = () => {
    setIsFiltersOpen(false);
  };

  const listOfFilters = useFiltersList(
    openedFiltersList,
    isCaMarket,
    handleAddFilter,
    handleExposeFilter,
  );

  const isPassedNav = useScrollTracker(72);

  const handleClearAll = () => {
    const region = offersList[0]?.zipClusterID;
    filterClearAll(confirmedFilters, region);
    dispatch(clearAllFilters());
    filterCurrentState(INTIAL_FILTERS);
  };

  return (
    <FiltersWrapper ref={listRef}>
      <FilterHeadWrapper>
        <TitleAndClose>
          <Headline
            data-testid="filters-title"
            tag="h3"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'normal',
            }}
          >
            {t('promo.filters.title')}
          </Headline>
          <Title />
          <HideFilters
            onClick={() => {
              setIsFiltersOpen(false);
            }}
          >
            {isLaptop ? (
              <>
                <BackIcon />
                {t('promo.filters.hideFilters')}
              </>
            ) : (
              <CloseIcon />
            )}
          </HideFilters>
        </TitleAndClose>
      </FilterHeadWrapper>
      <FiltersList isReachedEndOfContainer={isReachedEndOfContainer} isPassedNav={isPassedNav}>
        {!isCaMarket && <ZipInput />}
        <Accordion>
          {listOfFilters.map((vehicleFilter) => (
            <div key={vehicleFilter.value}>{vehicleFilter.Component}</div>
          ))}
        </Accordion>
      </FiltersList>
      {!isLaptop && (
        <CtaGroup
          totalCount={totalAvailableOffers}
          closeFilter={handleCloseFilters}
          onReset={handleClearAll}
        />
      )}
    </FiltersWrapper>
  );
};

export default Filters;
